import {Injectable} from '@angular/core';
import {Observable} from 'rxjs';
import {UserInfo} from './user-info.model';
import {URL_BASE} from '../../core/constants';
import {HttpClient, HttpHeaders} from '@angular/common/http';
import {HttpUtils} from '../../shared/http-utils';
import {UserCacheService, UserListType} from './user-cache.service';
import * as moment from 'moment';
import {map, tap} from 'rxjs/operators';
import {Report} from '../../reports/shared/report.model';
import {Role} from './role.model';
import {SearchObjectResult} from '../../shared/search-object-result.model';
import {LocalLocationContainerLight} from '../../local-loads/shared/local-location-container-light.model';
import {Region} from './region.model';
import {CMBLLangCode} from '../../core/languages';
import {UserSearchCriteria} from '../../shared/user-search-criteria';
import {UserSearch} from './user-search.model';
import {SortDirection} from '../../shared/sort-direction';
import {UserProfile} from '../../shared/user-profile';

@Injectable()
export class UserService {

  private USERS_URI                 = 'api/users';
  private usersCacheListType        = UserListType.USERS;
  private adminsCacheListType       = UserListType.ADMINS;
  private chepServiceRepCacheType   = UserListType.CHEP_SERVICE_REP;
  private chepManagersCacheListType = UserListType.CHEP_MANAGERS;
  private plantManagersCacheListType = UserListType.CHEP_MANAGERS;

  constructor(private http: HttpClient, private cacheService: UserCacheService) {
  }

  public getAll(): Observable<UserInfo[]> {
    const headers = HttpUtils.createHeaders();
    const url     = `${URL_BASE}/${this.USERS_URI}`;
    return this.http.get(url, { headers })
      .pipe(
        map((users: any[]) => users.map(user => UserInfo.fromDto(user))),
        map(users => this.cacheService.aggregate(users, this.usersCacheListType))
      );
  }

  public searchAll(page: number, pageSize: number): Observable<SearchObjectResult<UserInfo>> {
    const headers = HttpUtils.createHeaders();
    const params = { page: page.toString(), size: pageSize.toString() };
    const url = `${URL_BASE}/${this.USERS_URI}/search`;
    return this.http.get(url, {headers, params})
      .pipe(
        map(result => SearchObjectResult.fromDto(result, UserInfo.fromDto))
      );
  }

  public searchByCriteria(page: number, pageSize: number, direction: SortDirection, criteria: UserSearchCriteria): Observable<SearchObjectResult<UserSearch>> {
    const headers = HttpUtils.createHeaders();
    const params = criteria.toQueryParamsWithDirection(page, pageSize, direction);

    const url = `${URL_BASE}/${this.USERS_URI}/criteriaSearch`;
    return this.http.get(url, {headers, params})
      .pipe(map(result => SearchObjectResult.fromDto(result, UserSearch.fromDto)));
  }

  public searchChepManagers(regions: Region[], page: number, pageSize: number, filters: UserSearchCriteria = new UserSearchCriteria()) {
    const headers = HttpUtils.createHeaders();
    const params = filters.toQueryParams(page, pageSize);

    const url = `${URL_BASE}/${this.USERS_URI}/chepManagers/search`;
    return this.http.get(url, {headers, params})
      .pipe(
        map(result => SearchObjectResult.fromDto(result, UserInfo.fromDto)),
        map(result => {
          result.results.forEach(u =>
            u.regions = regions.filter(r => u.allowedRegions.includes(r.id))
          );
          return result;
        })
      );
  }

  public getChepManagers(): Observable<UserInfo[]> {
    const headers = HttpUtils.createHeaders();
    const url     = `${URL_BASE}/${this.USERS_URI}/chepManagers`;
    return this.http.get(url, { headers })
      .pipe(
        map((users: any[]) => users.map(user => UserInfo.fromDto(user))),
        map(users => this.cacheService.aggregate(users, this.chepManagersCacheListType))
      );
  }

  public searchChepRep(page: number, pageSize: number, filters: UserSearchCriteria = new UserSearchCriteria()) {
    const headers = HttpUtils.createHeaders();
    const params = filters.toQueryParams(page, pageSize);

    const url = `${URL_BASE}/${this.USERS_URI}/chepServiceRepresentatives/search`;

    return this.http.get(url, {headers, params})
      .pipe(
        map(result => SearchObjectResult.fromDto(result, UserInfo.fromDto))
      );
  }

  public getChepRep(): Observable<UserInfo[]> {
    const headers = HttpUtils.createHeaders();

    const url     = `${URL_BASE}/${this.USERS_URI}/chepServiceRepresentatives`;

    return this.http.get(url, { headers })
      .pipe(
        map((users: any[]) => users.map(user => UserInfo.fromDto(user))),
        map(users => this.cacheService.aggregate(users, this.chepManagersCacheListType))
      );
  }

  public getPlantManagers(): Observable<UserInfo[]> {
    const headers = HttpUtils.createHeaders();
    const url     = `${URL_BASE}/${this.USERS_URI}/plantManagers`;
    return this.http.get(url, { headers })
      .pipe(
        map((users: any[]) => users.map(user => UserInfo.fromDto(user))),
        map(users => this.cacheService.aggregate(users, this.plantManagersCacheListType))
      );
  }

  public searchPlantManagers(plants: LocalLocationContainerLight[], page: number, pageSize: number, filters: UserSearchCriteria = new UserSearchCriteria()) {
    const headers = HttpUtils.createHeaders();
    const params = filters.toQueryParams(page, pageSize);

    return this.http.get(`${URL_BASE}/${this.USERS_URI}/plantManagers/search`, {headers, params})
      .pipe(
        map(result => SearchObjectResult.fromDto(result, UserInfo.fromDto)),
        map(result => {
          result.results.forEach(u => u.plants = plants.filter(p => u.allowedPlants.includes(p.id)));
          return result;
        })
      );
  }

  public getAdministrators(): Observable<UserInfo[]> {
    const headers = HttpUtils.createHeaders();
    const url     = `${URL_BASE}/${this.USERS_URI}/administrators`;
    return this.http.get(url, { headers })
      .pipe(
        map((users: any[]) => users.map(user => UserInfo.fromDto(user))),
        map(users => this.cacheService.aggregate(users, this.adminsCacheListType))
      );
  }

  public searchAdministrators(page: number, pageSize: number, filters: UserSearchCriteria = new UserSearchCriteria()) {
    const headers = HttpUtils.createHeaders();
    const params = filters.toQueryParams(page, pageSize);

    return this.http.get(`${URL_BASE}/${this.USERS_URI}/administrators/search`, {headers, params})
      .pipe(
        map(result => SearchObjectResult.fromDto(result, UserInfo.fromDto))
      );
  }

  public getById(userId: string): Observable<UserInfo> {
    const headers = HttpUtils.createHeaders();
    const url     = `${URL_BASE}/${this.USERS_URI}/${userId}`;
    return this.http.get(url, { headers })
      .pipe(
        map(user => UserInfo.fromDto(user))
      );
  }

  public getSelf(): Observable<UserInfo> {
    const headers = HttpUtils.createHeaders();
    const url     = `${URL_BASE}/${this.USERS_URI}/self`;
    return this.http.get(url, { headers })
      .pipe(
        map(user => UserInfo.fromDto(user))
      );
  }

  public getSelfReports(): Observable<Report[]> {
    const headers = HttpUtils.createHeaders();
    const url     = `${URL_BASE}/${this.USERS_URI}/self/reports`;
    return this.http.get(url, { headers })
      .pipe(
        map((reports: any[]) => reports.map(p => Report.fromDto(p)))
      );
  }

  public getHomeReport(): Observable<Report> {
    const headers = HttpUtils.createHeaders();
    return this.http.get(`${URL_BASE}/${this.USERS_URI}/self/homeReport`, {headers})
      .pipe(
        map(r => Report.fromDto(r))
      );
  }

  public createUser(user: UserInfo): Observable<UserInfo> {
    const headers = HttpUtils.createHeaders();
    const url     = `${URL_BASE}/${this.USERS_URI}`;
    return this.http.post(url, UserInfo.toDto(user), { headers })
      .pipe(
        map(u => UserInfo.fromDto(u)),
        tap(u => this.cacheService.registerNewUser(u, this.usersCacheListType))
      );
  }

  public createChepManager(user: UserInfo): Observable<UserInfo> {
    const headers = HttpUtils.createHeaders();
    const url     = `${URL_BASE}/${this.USERS_URI}/chepManagers`;
    return this.http.post(url, UserInfo.toDto(user), { headers })
      .pipe(
        map(u => UserInfo.fromDto(u)),
        tap(u => this.cacheService.registerNewUser(u, this.chepManagersCacheListType))
      );
  }

  public createPlantManager(user: UserInfo): Observable<UserInfo> {
    const headers = HttpUtils.createHeaders();
    const url     = `${URL_BASE}/${this.USERS_URI}/plantManagers`;
    return this.http.post(url, UserInfo.toDto(user), { headers })
      .pipe(
        map(u => UserInfo.fromDto(u)),
        tap(u => this.cacheService.registerNewUser(u, this.plantManagersCacheListType))
      );
  }

  public createAdministrator(user: UserInfo): Observable<UserInfo> {
    const headers = HttpUtils.createHeaders();
    const url     = `${URL_BASE}/${this.USERS_URI}/administrators`;
    return this.http.post(url, UserInfo.toDto(user), { headers })
      .pipe(
        map(u => UserInfo.fromDto(u)),
        tap(u => this.cacheService.registerNewUser(u, this.adminsCacheListType))
      );
  }

  public updateUser(user: UserInfo): Observable<UserInfo> {
    return this.update(user, this.usersCacheListType);
  }

  public updateAdministrator(user: UserInfo): Observable<UserInfo> {
    return this.update(user, this.adminsCacheListType);
  }

  public updateChepManager(user: UserInfo): Observable<UserInfo> {
    return this.update(user, this.chepManagersCacheListType);
  }

  public updatePlantManager(user: UserInfo): Observable<UserInfo> {
    return this.update(user, this.plantManagersCacheListType);
  }

  private update(user: UserInfo, cacheListType: UserListType): Observable<UserInfo> {
    const headers = HttpUtils.createHeaders();
    const url     = `${URL_BASE}/${this.USERS_URI}/${user.id}`;
    return this.http.put(url, UserInfo.toDto(user), { headers })
      .pipe(
        map(u => UserInfo.fromDto(u)),
        tap(u => this.cacheService.registerUpdatedUser(u, cacheListType))
      );
  }

  public updatePassword(user: UserInfo, password: string): Observable<void> {
    const headers = HttpUtils.createHeaders();
    const url     = `${URL_BASE}/${this.USERS_URI}/${user.id}/password`;
    return this.http.post<void>(url, { password }, { headers });
  }

  public updateLanguage(langCode: CMBLLangCode): Observable<void> {
    const headers = HttpUtils.createHeaders();
    const url     = `${URL_BASE}/${this.USERS_URI}/self/language`;
    const payload = { lang_code: langCode };
    return this.http.post<void>(url, payload, { headers });
  }

  public deleteUser(userId: string): Observable<boolean> {
    return this.delete(userId, this.usersCacheListType);
  }

  public deleteAdministrator(userId: string): Observable<boolean> {
    return this.delete(userId, this.adminsCacheListType);
  }

  public deleteChepManager(userId: string): Observable<boolean> {
    return this.delete(userId, this.chepManagersCacheListType);
  }

  public deletePlantManager(userId: string): Observable<boolean> {
    return this.delete(userId, this.plantManagersCacheListType);
  }

  private delete(userId: string, cacheListType: UserListType): Observable<boolean> {
    const headers = HttpUtils.createHeaders();
    const url     = `${URL_BASE}/${this.USERS_URI}/${userId}`;
    return this.http.delete<boolean>(url, { headers })
      .pipe(
        tap(() => this.cacheService.registerRemovedUser(userId, cacheListType))
      );
  }

  public toggleBlockingUser(user: UserInfo): Observable<boolean> {
    return this.toggleBlocking(user, this.usersCacheListType);
  }

  public toggleBlockingAdministrator(user: UserInfo): Observable<boolean> {
    return this.toggleBlocking(user, this.adminsCacheListType);
  }

  public toggleBlockingChepManager(user: UserInfo): Observable<boolean> {
    return this.toggleBlocking(user, this.chepManagersCacheListType);
  }

  public toggleBlockingPlantManager(user: UserInfo): Observable<boolean> {
    return this.toggleBlocking(user, this.plantManagersCacheListType);
  }

  private toggleBlocking(user: UserInfo, cacheListType: UserListType): Observable<boolean> {
    const updateDate = moment();

    const headers = HttpUtils.createHeaders();
    const url     = `${URL_BASE}/${this.USERS_URI}/${user.id}/blocked/${!user.blocked}`;
    return this.http.post<boolean>(url, null, { headers })
      .pipe(
        tap(() => {
          user.updateDate          = updateDate.toISOString();
          user.updateDateTimestamp = Number(updateDate.format('x'));
          this.cacheService.registerUpdatedUser(user, cacheListType);
        })
      );
  }

  public unblockIpUser(user: UserInfo): Observable<boolean> {
    return this.unblockIp(user, this.usersCacheListType);
  }

  public unblockIpAdministrator(user: UserInfo): Observable<boolean> {
    return this.unblockIp(user, this.adminsCacheListType);
  }

  public unblockIpChepManager(user: UserInfo): Observable<boolean> {
    return this.unblockIp(user, this.chepManagersCacheListType);
  }

  public unblockIpPlantManager(user: UserInfo): Observable<boolean> {
    return this.unblockIp(user, this.plantManagersCacheListType);
  }

  public unblockIp(user: UserInfo, cacheListType: UserListType): Observable<boolean> {
    const updateDate = moment();

    const headers = HttpUtils.createHeaders();
    const url     = `${URL_BASE}/${this.USERS_URI}/${user.id}/unblockFromAnomaly`;
    return this.http.post<boolean>(url, null, { headers })
      .pipe(
        map(result => {
          user.updateDate          = updateDate.toISOString();
          user.updateDateTimestamp = Number(updateDate.format('x'));
          this.cacheService.registerUpdatedUser(user, cacheListType);
          return result;
        })
      );
  }

  public getRoles(id: string): Observable<Role[]> {
    const headers = HttpUtils.createHeaders();
    const url     = `${URL_BASE}/${this.USERS_URI}/${id}/roles`;
    return this.http.get(url, { headers })
      .pipe(
        map((roles: any[]) => roles.map(role => Role.fromDto(role)))
      );
  }

  public removeUserFromRole(userId: string, roleId: string): Observable<void> {
    const headers = HttpUtils.createHeaders();
    const url     = `${URL_BASE}/api/roles/${roleId}/users/${userId}`;
    return this.http.delete<void>(url, { headers });
  }

  public addUserToRole(userId: string, roleIdpid: string): Observable<void> {
    const headers = HttpUtils.createHeaders();
    const url     = `${URL_BASE}/${this.USERS_URI}/${userId}/roles/${roleIdpid}`;
    return this.http.post<void>(url, null, { headers });
  }

  // Chep Service Rep

  public createChepServiceRep(user: UserInfo): Observable<UserInfo> {
    const headers = HttpUtils.createHeaders();
    const url     = `${URL_BASE}/${this.USERS_URI}/chepServiceRepresentative`;
    return this.http.post(url, UserInfo.toDto(user), { headers })
      .pipe(
        map(u => UserInfo.fromDto(u)),
        tap(u => this.cacheService.registerNewUser(u, this.chepManagersCacheListType))
      );
  }

  public updateChepServiceRep(user: UserInfo): Observable<UserInfo> {
    return this.update(user, this.chepServiceRepCacheType);
  }

  public deleteChepServiceRep(id: string): Observable<boolean> {
    return this.delete(id, this.chepServiceRepCacheType);
  }

  public toggleBlockingChepServiceRep(user: UserInfo): Observable<boolean> {
    return this.toggleBlocking(user, this.chepServiceRepCacheType);
  }

  public unblockIpChepServiceRep(user: UserInfo): Observable<boolean> {
    return this.unblockIp(user, this.chepServiceRepCacheType);
  }

  public getSelfUserProfile(accessToken: string): Observable<UserProfile> {
    const url     = `${URL_BASE}/api/authenticate/self/userProfile`;
    const headers = new HttpHeaders().set('x-token', `${accessToken}`);
    return this.http.get(url, { headers })
      .pipe(map(user => UserProfile.fromDto(user)));
  }
}
